import { useEffect, useRef, useContext } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  isWebPushSupported,
  getWebPushPermission,
  requestWebPushPermission,
  subscribeWebPush,
  unsubscribeWebPush,
  ensureWebPushSubscribed
} from "../../utils/webPushManager";
import isEquipeChatMobileApp from "../../utils/isEquipeChatMobileApp";

// Não ativa no APK (já usa FCM nativo)
const shouldActivate = () => isWebPushSupported() && !isEquipeChatMobileApp();

const WebPushManager = () => {
  const { isAuth, user } = useContext(AuthContext);
  const subscribedRef = useRef(false);
  const userIdRef = useRef(null);

  useEffect(() => {
    if (!shouldActivate() || !isAuth || !user?.id) {
      return;
    }

    // Novo login: resubscreve
    if (userIdRef.current !== user.id) {
      subscribedRef.current = false;
      userIdRef.current = user.id;
    }

    if (subscribedRef.current) return;

    const setup = async () => {
      const permission = getWebPushPermission();

      if (permission === "denied") return;

      if (permission === "default") {
        // Pede permissão após pequeno delay para não assustar o usuário na abertura
        await new Promise((r) => setTimeout(r, 3000));
        const granted = await requestWebPushPermission();
        if (!granted) return;
      }

      const ok = await subscribeWebPush();
      if (ok) {
        subscribedRef.current = true;
      }
    };

    setup();
  }, [isAuth, user?.id]);

  // Ao fazer logout, cancela a subscrição
  useEffect(() => {
    if (!shouldActivate()) return;

    if (!isAuth && subscribedRef.current) {
      unsubscribeWebPush();
      subscribedRef.current = false;
      userIdRef.current = null;
    }
  }, [isAuth]);

  // Ao retomar o app (foco na aba), re-sincroniza subscrição
  useEffect(() => {
    if (!shouldActivate() || !isAuth) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        ensureWebPushSubscribed();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isAuth]);

  return null;
};

export default WebPushManager;
